import { AlertService } from './../../shared/services/alert.service';
import { Injectable } from '@angular/core';
import { EmployeeService } from '../../shared/services/employee.service';
import { Action, Store } from '@ngrx/store';
import { Observable } from 'rxjs/internal/Observable';
import * as fromActions from './employee.actions';
import * as fromAdapter from './employee.adapter';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs/internal/observable/of';
import {Department, IEmployee, IResponse, ISyncStatus} from '@signal/asp-data-commons';
import { toData } from '../app.adapter';
import * as fromServiceActions from './../service-appointment/service-appointment.actions';
import { ServiceAppointmentState } from '../service-appointment/service-appointment.reducer';

@Injectable()
export class EmployeeEffects {
  somethingWentWrongPleaseTryAgain =  'otherTexts.somethingWentWrongPleaseTryAgain';
  
  constructor(
    private actions$: Actions,
    private employeeService: EmployeeService,
    private alertService: AlertService,
    private serviceAppointmentStore : Store<ServiceAppointmentState>
  ) {
  }

  
  loadEmployees: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.LoadEmployees>(
      fromActions.EmployeeActionTypes.LOAD_EMPLOYEES
    ),
    mergeMap((action: fromActions.LoadEmployees) =>
      this.employeeService.getEmployees().pipe(
        switchMap((employees: IResponse<IEmployee[]>) => toData(employees)),
        map((employees: IEmployee[]) => new fromActions.LoadEmployeesSuccess({ employees })),
        catchError(err => of(new fromActions.LoadEmployeesFail({ error: err })))
      )
    )
  ));

  
  loadEmployee: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.LoadEmployee>(
      fromActions.EmployeeActionTypes.LOAD_EMPLOYEE
    ),
    map((action: fromActions.LoadEmployee) => action.payload.employeeId),
    mergeMap((employeeId: string) =>
      this.employeeService.getEmployee(employeeId).pipe(
        switchMap((employee: IResponse<IEmployee>) => toData(employee)),
        map(
          (loadedEmployee: IEmployee) =>
            new fromActions.LoadEmployeeSuccess({ employee: loadedEmployee }),
        ),
        catchError(err => of(new fromActions.LoadEmployeesFail({ error: err })))
      )
    )
  ));

  
  updateEmployee: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.UpdateEmployee>(
      fromActions.EmployeeActionTypes.UPDATE_EMPLOYEE
    ),
    map((action: fromActions.UpdateEmployee) => action.payload),
    mergeMap(({employee, isLoadNotificationData}) =>
      this.employeeService.replaceEmployee(employee.id, fromAdapter.singleEmployeeAvatarUrlEncoding(employee)).pipe(
        switchMap((updatedEmployee: IResponse<IEmployee>) => toData(updatedEmployee)),
        map(
          (updatedEmployee: IEmployee) => {
            if (isLoadNotificationData && (updatedEmployee.department === Department.SERVICE
              || updatedEmployee.department === Department.SALES
              || updatedEmployee.department === Department.PARTS)) {
              this.serviceAppointmentStore.dispatch(new fromServiceActions.LoadNotificationDashboardData({department: updatedEmployee.department}));
            }
            const titleTextMap: Record<string, string> = {
              'Service Advisor': 'otherTexts.serviceAdvisorDetailsUpdatedSuccessfully',
              'Sales Advisor': 'otherTexts.salesAdvisorDetailsUpdatedSuccessfully',
              'Parts Advisor': 'otherTexts.partsAdvisorDetailsUpdatedSuccessfully',
            };
            const text = updatedEmployee.title
              ? (titleTextMap[updatedEmployee.title] ?? 'otherTexts.serviceTechnicianDetailsUpdatedSuccessfully')
              : 'otherTexts.detailsUpdatedSuccessfully.';
            this.alertService.showAlert(text, '', '', 5000, false);
            return new fromActions.UpdateEmployeeSuccess({ employee: updatedEmployee })
          },
        ),
        catchError(err => {
          if(err.error?.status?.messages?.length){
            this.alertService.showAlert(err.error?.status?.messages[0]);
            return of(new fromActions.UpdateEmployeeFail({ error: err }));
          }
          else{
            this.alertService.showAlert(this.somethingWentWrongPleaseTryAgain);
            return of(new fromActions.UpdateEmployeeFail({ error: err }));
          }
        })
      ))));

  // @Effect()
  // updateTeamDetails: Observable<Action> = this.actions$.pipe(
  //   ofType<fromActions.UpdateTeamDetails>(
  //     fromActions.EmployeeActionTypes.UPDATE_TEAM_DETAILS
  //   ),
  //   map((action: fromActions.UpdateTeamDetails) => action.payload.employees),
  //   mergeMap((employeeUpdates: IEmployee[]) =>
  //     this.employeeService.putEmployees(fromAdapter.employeeAvatarUrlEncoding(employeeUpdates)).pipe(
  //       switchMap((updatedEmployee: IResponse<IEmployee>) => toData(updatedEmployee)),
  //       map(
  //         (updatedEmployee: IEmployee) => {
  //           this.alertService.showAlert('Team details updated successfully');
  //           return new fromActions.UpdateEmployeeSuccess({ employee: updatedEmployee })
  //         },
  //       ),
  //       catchError(err => {
  //         this.alertService.showAlert('Something went wrong please try again');
  //         return of(new fromActions.LoadEmployeesFail({ error: err }))
  //       })
  //     )));

  
  loadStaffMasterSyncStatus: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.LoadStaffMasterSyncStatus>(
      fromActions.EmployeeActionTypes.LOAD_STAFF_MASTER_SYNC_STATUS
    ),
    mergeMap((action: fromActions.LoadStaffMasterSyncStatus) =>
      this.employeeService.getStaffMasterSyncStatus().pipe(
        switchMap((syncStatus: IResponse<ISyncStatus>) => toData(syncStatus)),
        map(
          (syncStatus: ISyncStatus) => {
            // this.alertService.showAlert('Sync status updated');
            return new fromActions.LoadStaffMasterSyncStatusSuccess({syncStatus});
          },
        ),
        catchError(error => {
          this.alertService.showAlert(this.somethingWentWrongPleaseTryAgain);
          return of(new fromActions.LoadStaffMasterSyncStatusFail({error}));
        })
      ))));

  
  triggerStaffMasterSync: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.TriggerStaffMasterSync>(
      fromActions.EmployeeActionTypes.TRIGGER_STAFF_MASTER_SYNC
    ),
    mergeMap((action: fromActions.TriggerStaffMasterSync) =>
      this.employeeService.triggerStaffMasterSync().pipe(
        switchMap((syncStatus: IResponse<ISyncStatus>) => toData(syncStatus, 201)),
        map(
          (syncStatus: ISyncStatus) => {
            // this.alertService.showAlert('Sync status updated');
            return new fromActions.LoadStaffMasterSyncStatusSuccess({syncStatus});
          },
        ),
        catchError(error => {
          this.alertService.showAlert(this.somethingWentWrongPleaseTryAgain);
          return of(new fromActions.LoadStaffMasterSyncStatusFail({error}));
        })
      ))));

      
      loadPresignedUrl: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadPreSignedUrl>(
          fromActions.EmployeeActionTypes.LOAD_PRESIGNED_URL
        ),
        switchMap((action: fromActions.LoadPreSignedUrl) => {
          const payload = action instanceof fromActions.LoadPreSignedUrl ? action.payload : undefined;
          return this.employeeService.LoadPreSignedUrl(payload.empId,payload.imageFormat).pipe(
            switchMap((preSignedUrl: IResponse<string>) => toData(preSignedUrl)),
            map((preSignedUrl: string) => new fromActions.LoadPreSignedUrlSuccess({ data:preSignedUrl })),
            catchError(err => of(new fromActions.LoadPreSignedUrlFail({ error: err })))
          )}
        )
      ));
  ToTitleCase(text:string){
    return  text ? text.toString().charAt(0)?.toUpperCase()+text?.toString()?.substr(1)?.toLowerCase() : '';
  }

}

